import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect } from 'react';
import { useMyPendingInvitations, type MyPendingInvitation } from '@/hooks/useInvitation';
import { BrandedSpinner } from '@/components/BrandedSpinner';
import Button from '@/components/Button';
import { ArrowRight } from 'lucide-react';

export const Route = createFileRoute('/_auth/pending-invitations')({
  component: PendingInvitationsPage,
});

function sponsorDisplay(inv: MyPendingInvitation): string {
  const name = inv.parentUserName?.trim();
  if (name) return name;
  return inv.fromEmail;
}

function PendingInvitationsPage() {
  const navigate = useNavigate();
  const { data: pending, isLoading, isError } = useMyPendingInvitations();

  // Defensive redirects: if the list collapsed since the redirect component
  // fired (e.g. user accepted one in another tab, or one expired), don't
  // strand them on an empty / single-item picker.
  useEffect(() => {
    if (isLoading || !pending) return;
    if (pending.length === 0) {
      navigate({ to: '/', search: { invitationcode: undefined, __clerk_ticket: undefined } });
      return;
    }
    if (pending.length === 1) {
      navigate({
        to: '/invite',
        search: { invitationcode: pending[0].invitationCode, __clerk_ticket: undefined },
      });
    }
  }, [isLoading, pending, navigate]);

  if (isLoading || !pending || pending.length < 2) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-white px-4">
        <BrandedSpinner size="xl" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white px-4">
        <div className="max-w-md w-full text-center">
          <h1 className="text-2xl font-semibold text-gray-900 mb-3">Something went wrong</h1>
          <p className="text-gray-500 mb-8">
            We couldn't load your invitations. Please try again in a moment.
          </p>
          <Button
            variant="primary"
            size="xlarge"
            fullWidth
            onClick={() => window.location.reload()}
          >
            Try again
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-white px-4 py-12">
      <div className="max-w-md w-full">
        <h1
          className="font-semibold text-gray-900 leading-tight tracking-tight mb-3 text-center"
          style={{ fontSize: 'clamp(1.8rem, 5vw, 2.4rem)' }}
        >
          You've been invited to {pending.length} family plans
        </h1>
        <p className="text-lg text-gray-500 mb-8 leading-relaxed text-center">
          Choose which one you'd like to join. You can only be on one family plan at a time.
        </p>

        <div className="space-y-3">
          {pending.map((inv) => (
            <div
              key={inv.invitationCode}
              className="rounded-xl border border-slate-200 bg-white px-5 py-4 flex flex-col gap-3"
            >
              <div>
                <p className="text-sm text-slate-500">Invited by</p>
                <p className="text-lg font-medium text-gray-900">{sponsorDisplay(inv)}</p>
              </div>
              <Button
                variant="primary"
                size="large"
                fullWidth
                onClick={() =>
                  navigate({
                    to: '/invite',
                    search: { invitationcode: inv.invitationCode, __clerk_ticket: undefined },
                  })
                }
              >
                Join their family plan <ArrowRight className="w-4 h-4 inline" />
              </Button>
            </div>
          ))}
        </div>

        <div className="mt-8 text-center">
          <button
            type="button"
            onClick={() =>
              navigate({ to: '/', search: { invitationcode: undefined, __clerk_ticket: undefined } })
            }
            className="text-sm text-slate-500 hover:text-slate-700 underline-offset-4 hover:underline"
          >
            Not right now
          </button>
        </div>
      </div>
    </div>
  );
}
